import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Circle, CheckCircle2, ListTodo } from 'lucide-react';
import { TASK_STATUS_DONE } from '@epasu/core/constants';
import { crm, type TaskRow } from '@/lib/crm-client';
import { cn } from '@/lib/utils';
import { ListenKarte } from './ListenKarte';
import { ZEILEN, heuteBeginn } from './useDashboardData';

/**
 * Was heute zu tun ist — und abhakbar, ohne die Seite zu wechseln.
 *
 * Das Abhaken ist der Grund, warum die Liste hier steht und nicht bloß eine
 * Zahl: Die Hälfte der fälligen Aufgaben ist morgens schon erledigt, und dafür
 * erst auf die Aufgabenseite zu wechseln, ist ein Umweg.
 */
export function TasksCard({ tasks, onChanged }: { tasks: TaskRow[]; onChanged: () => void }) {
  const navigate = useNavigate();
  const [laeuft, setLaeuft] = useState<string | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);

  const sichtbar = tasks.slice(0, ZEILEN);
  const grenze = heuteBeginn();

  const abhaken = async (task: TaskRow) => {
    setFehler(null);
    setLaeuft(task.id);
    try {
      await crm.tasks.setStatus(task.id, TASK_STATUS_DONE);
      onChanged();
    } catch (err) {
      setFehler(err instanceof Error ? err.message : 'Abhaken fehlgeschlagen');
    } finally {
      setLaeuft(null);
    }
  };

  return (
    <ListenKarte
      titel="Heute fällig"
      icon={ListTodo}
      mehr={{ label: 'Alle Aufgaben', onClick: () => navigate('/aufgaben') }}
      leer="Nichts fällig heute."
      gesamt={tasks.length}
      gezeigt={sichtbar.length}
    >
      {fehler && (
        <li className="px-4 py-2 text-xs text-destructive">{fehler}</li>
      )}
      {sichtbar.map((task) => {
        const ueberfaellig = task.dueAt !== null && task.dueAt < grenze;
        return (
          <li key={task.id} className="flex items-center gap-3 px-4 py-2.5">
            <button
              onClick={() => void abhaken(task)}
              disabled={laeuft === task.id}
              title="Als erledigt abhaken"
              className="group shrink-0 text-muted-foreground transition-colors hover:text-success disabled:opacity-40"
            >
              <Circle className="h-4 w-4 group-hover:hidden" />
              <CheckCircle2 className="hidden h-4 w-4 group-hover:block" />
            </button>

            <button
              onClick={() => navigate('/aufgaben')}
              className="min-w-0 flex-1 text-left"
            >
              <p className="truncate text-sm">{task.title}</p>
              {task.contactName && (
                <p className="truncate text-xs text-muted-foreground">{task.contactName}</p>
              )}
            </button>

            <span
              className={cn(
                'shrink-0 text-xs',
                ueberfaellig ? 'font-medium text-danger' : 'text-muted-foreground',
              )}
            >
              {ueberfaellig ? 'überfällig' : 'heute'}
            </span>
          </li>
        );
      })}
    </ListenKarte>
  );
}
